import React, { useMemo, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { GridRowSelectionModel } from '@mui/x-data-grid';

import { useCustomColumnEdit } from '../../../hooks/useCustomColumnEdit';
import { AssetInventoryGridColDef } from '../../../types';

import { EditCellValueDialog } from '../../dialogs/editCellValueDialog';

export function CustomColumnsMultiEdit({
  clearSelectedRows,
  columns,
  selectedRows,
  teamName,
}: {
  clearSelectedRows: () => void;
  columns: AssetInventoryGridColDef[];
  selectedRows: GridRowSelectionModel;
  teamName: string;
}) {
  const [selectedColumnToEdit, setSelectedColumnToEdit] = useState<
    AssetInventoryGridColDef | undefined
  >(undefined);
  const { editCustomColumnValues } = useCustomColumnEdit();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const editableColumns = useMemo(() => columns.filter((col) => col.editable), [columns]);
  const showColumnSelect = editableColumns && editableColumns?.length > 2;

  const handleViewMoreColumns = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMoreColumnsList = () => {
    setAnchorEl(null);
  };

  const handleInitEdit = (column: AssetInventoryGridColDef) => {
    setSelectedColumnToEdit(column);
  };

  const handleCloseEdit = () => {
    setSelectedColumnToEdit(undefined);
  };

  const handleSaveEdit = async (value: any) => {
    await editCustomColumnValues({
      customColumnId: selectedColumnToEdit!.id!,
      rowIds: [...selectedRows.ids] as string[],
      successMessage: `New ${selectedColumnToEdit!.headerName} has been saved in ${selectedRows.ids.size} resources.`,
      teamName,
      value,
    });

    handleCloseEdit();
  };

  if (selectedRows.ids.size < 2 || editableColumns?.length === 0) {
    return null;
  }

  return (
    <>
      {selectedColumnToEdit && (
        <EditCellValueDialog
          column={selectedColumnToEdit}
          onClose={handleCloseEdit}
          onSave={handleSaveEdit}
          open={!!selectedColumnToEdit}
          resourceCount={selectedRows.ids.size}
        />
      )}
      <Box
        bgcolor={'neutral.700'}
        borderRadius={2}
        bottom={20}
        left={'50%'}
        position={'fixed'}
        px={2}
        py={1.5}
        sx={{
          transform: 'translateX(-50%)',
        }}
        width={650}
      >
        <Stack alignItems="center" direction="row" justifyContent="space-between" spacing={2}>
          <Stack alignItems="center" direction="row" spacing={1}>
            <Typography>{`${selectedRows.ids.size} resources selected`}</Typography>
            <Button onClick={clearSelectedRows} sx={{ color: 'text.secondary' }} variant="text">
              Clear selection
            </Button>
          </Stack>
          <Stack direction="row" spacing={1}>
            <Button
              color="inherit"
              onClick={() => handleInitEdit(editableColumns?.[0])}
              size="small"
              variant="contained"
            >
              Update {editableColumns?.[0].headerName}
            </Button>
            {!showColumnSelect && editableColumns.length === 2 && (
              <Button
                color="inherit"
                onClick={() => handleInitEdit(editableColumns?.[1])}
                size="small"
                variant="contained"
              >
                Update {editableColumns?.[1].headerName}
              </Button>
            )}
            {showColumnSelect && (
              <>
                <Button
                  aria-controls={open ? 'demo-customized-menu' : undefined}
                  aria-expanded={open ? 'true' : undefined}
                  aria-haspopup="true"
                  color="inherit"
                  disableElevation={true}
                  endIcon={<KeyboardArrowDownIcon />}
                  id="more-columns-button"
                  onClick={handleViewMoreColumns}
                  size="small"
                  variant="contained"
                >
                  More columns
                </Button>
                <Menu
                  anchorEl={anchorEl}
                  anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'top',
                  }}
                  MenuListProps={{
                    'aria-labelledby': 'more-columns-button',
                  }}
                  onClose={handleCloseMoreColumnsList}
                  open={open}
                  transformOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom',
                  }}
                >
                  {editableColumns.slice(1).map((column) => (
                    <MenuItem
                      key={column.field}
                      onClick={() => {
                        handleInitEdit(column);
                        handleCloseMoreColumnsList();
                      }}
                    >
                      {column.headerName}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
